import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight } from 'lucide-react';
import ProjectSelectModal from '../components/ProjectSelectModal';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const TOOL_IMAGES = {
  delivery: "https://customer-assets.emergentagent.com/job_7952493e-ecb0-4483-a1aa-f19d77930300/artifacts/ekpk2yty_Delivery.png",
  dmlBuilder: "https://customer-assets.emergentagent.com/job_7952493e-ecb0-4483-a1aa-f19d77930300/artifacts/rtc93b69_DML%20Builder%20Logo2.png",
  brexValidation: "https://customer-assets.emergentagent.com/job_7952493e-ecb0-4483-a1aa-f19d77930300/artifacts/hd7tcvfm_BREXValidation.png",
  brexBuilder: "https://customer-assets.emergentagent.com/job_4dda6c2c-c6cf-494a-b578-b0548b3af0ae/artifacts/d5198eca_BREXBuilder.png",
  author: "https://customer-assets.emergentagent.com/job_55de5930-e1fe-4683-9c65-c6190d26c152/artifacts/9fs2w7fm_Author.png",
  pdfPublish: "https://customer-assets.emergentagent.com/job_55de5930-e1fe-4683-9c65-c6190d26c152/artifacts/49fa80il_PDFPublish.png",
  steCheck: "https://customer-assets.emergentagent.com/job_55de5930-e1fe-4683-9c65-c6190d26c152/artifacts/vfvqnrzk_STECheck.png",
  conversion: "https://customer-assets.emergentagent.com/job_55de5930-e1fe-4683-9c65-c6190d26c152/artifacts/tl94uv0a_Conversion.png",
};

const ToolsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [allowedTools, setAllowedTools] = useState(null); // null = loading

  useEffect(() => {
    const fetchAllowed = async () => {
      try {
        const res = await fetch(`${API}/api/product-configs/my-tools`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setAllowedTools(data.tools);
        } else {
          setAllowedTools(null); // show all on error
        }
      } catch {
        setAllowedTools(null);
      }
    };
    if (token) fetchAllowed();
  }, [token]);

  const tools = [
    {
      id: 'delivery',
      name: 'Delivery',
      description: 'Compare DDN and DML files for delivery validation. Ensure consistency between dispatch notes and data module lists.',
      image: TOOL_IMAGES.delivery,
      path: '/dashboard/tools/delivery',
      features: ['DDN/DML Comparison', 'File Validation', 'Delivery History']
    },
    {
      id: 'dml-builder',
      name: 'DML Builder',
      description: 'Create and manage Data Module Lists. Build DMC codes with SNS, disassembly, and information code components.',
      image: TOOL_IMAGES.dmlBuilder,
      path: '/dashboard/tools/dml-builder',
      features: ['DMC Construction', 'Module Management', 'XML Generation']
    },
    {
      id: 'brex-validation',
      name: 'BREX Validation',
      description: 'Validate data modules against Business Rules Exchange documents. Check compliance with S1000D standards.',
      image: TOOL_IMAGES.brexValidation,
      path: '/dashboard/tools/brex-validation',
      features: ['Rule Validation', 'Compliance Checking', 'Validation History']
    },
    {
      id: 'brex-builder',
      name: 'BREX Builder',
      description: 'Build BREX data modules by configuring Business Rule Decision Points and generating BREX module definitions.',
      image: TOOL_IMAGES.brexBuilder,
      path: '/dashboard/tools/brex-builder',
      features: ['Decision Points', 'BREX Module', 'S1000D Compliance']
    },
    {
      id: 'author',
      name: 'Author',
      description: 'Create and edit S1000D data modules with a visual content editor. Run Spell Check, STE Check, and BREX compliance checks.',
      image: TOOL_IMAGES.author,
      path: '/dashboard/tools/author',
      features: ['Visual Editor', 'Content Blocks', 'Quality Checks']
    },
    {
      id: 'pdf-publish',
      name: 'PDF Publish',
      description: 'Publish data modules to PDF format with or without ID/Status sections. Supports custom XSL-FO stylesheets.',
      image: TOOL_IMAGES.pdfPublish,
      path: '/dashboard/tools/pdf-publish',
      features: ['PDF Generation', 'ID/Status Options', 'Custom Stylesheets']
    },
    {
      id: 'ste-check',
      name: 'STE Check',
      description: 'Check technical writing against Simplified Technical English (ASD-STE100) rules and project technical nouns.',
      image: TOOL_IMAGES.steCheck,
      path: '/dashboard/tools/ste-check',
      features: ['STE Compliance', 'Word Checking', 'Sentence Analysis']
    },
    {
      id: 'conversion',
      name: 'Conversion',
      description: 'Convert PDF and Word documents into S1000D Data Modules, Publication Modules, and ICNs using AI-powered analysis.',
      image: TOOL_IMAGES.conversion,
      path: '/dashboard/tools/conversion',
      features: ['PDF/Word Upload', 'AI Analysis', 'S1000D XML Generation']
    }
  ];

  const handleToolClick = (tool) => {
    setSelectedTool(tool);
    setShowProjectModal(true);
  };

  // Filter tools based on product configuration
  const visibleTools = allowedTools
    ? tools.filter(t => allowedTools.includes(t.id))
    : tools;

  const handleProjectSelect = (project) => {
    if (selectedTool) {
      // Navigate with project info in state
      navigate(selectedTool.path, { 
        state: { 
          project: project,
          projectId: project?.id || null,
          projectTitle: project?.title || null
        } 
      });
    }
  };

  return (
    <div className="space-y-8" data-testid="tools-page">
      {/* Page Header */}
      <div className="space-y-1">
        <h1 
          className="text-3xl font-bold tracking-tight text-slate-900"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Tools
        </h1>
        <p className="text-slate-600">
          Access S1000D documentation and validation tools
        </p>
      </div>

      {/* Tools Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {visibleTools.map((tool) => (
          <Card 
            key={tool.id}
            className="group relative overflow-hidden bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer"
            onClick={() => handleToolClick(tool)}
            data-testid={`tool-card-${tool.id}`}
          >
            {/* Tool Image */}
            <div className="aspect-square flex items-center justify-center p-8 bg-gradient-to-br from-slate-50 to-slate-100 group-hover:from-violet-50 group-hover:to-slate-50 transition-colors">
              <img 
                src={tool.image}
                alt={tool.name}
                className="w-full h-full object-contain max-w-[200px] max-h-[200px] drop-shadow-lg group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            
            {/* Tool Info */}
            <CardHeader className="pb-2">
              <CardTitle 
                className="text-xl flex items-center gap-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {tool.name}
                <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all text-violet-600" />
              </CardTitle>
              <CardDescription className="text-slate-500 text-sm line-clamp-2">
                {tool.description}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="pt-0">
              <div className="flex flex-wrap gap-2">
                {tool.features.map((feature) => (
                  <span 
                    key={feature}
                    className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-md group-hover:bg-violet-50 group-hover:text-violet-700 transition-colors"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Info Section */}
      <Card className="bg-slate-900 border-0 text-white">
        <CardContent className="p-6">
          <h3 
            className="text-xl font-semibold mb-2"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Tool Integration
          </h3>
          <p className="text-slate-300 text-sm">
            All tools are integrated with your project workspace. Select a project first to enable full functionality, 
            or use standalone mode for quick operations. Tool history is saved automatically for future reference.
          </p>
        </CardContent>
      </Card>

      {/* Project Selection Modal */}
      <ProjectSelectModal
        open={showProjectModal}
        onOpenChange={setShowProjectModal}
        onProjectSelect={handleProjectSelect}
        toolName={selectedTool?.name || 'this tool'}
      />
    </div>
  );
};

export default ToolsPage;
